/* eslint-disable react-refresh/only-export-components -- createApp is exported alongside MarketingWebsiteApp by design — the helper is the Gen 0 factory wrapper. */

/**
 * MarketingWebsiteApp — Phase O.7 canonical marketing-website for
 * PatrolPuffin at www.patrolpuffin.com.
 *
 * Replaces the hand-coded provider stack + router with
 * `@puffin-core/marketing-shell`'s `createMarketingApp` factory. The
 * factory bundles the BrowserRouter + I18nextProvider + Cookie consent
 * + Analytics + MarketingShell chrome (skip-link + ScrollToTop +
 * MarketingHeader + main + MarketingFooter), leaving this file as a thin
 * page-routes + brand-config descriptor.
 *
 * @module portals/marketing-website/MarketingWebsiteApp
 */

import { createFetchSeam } from '@puffin-core/api-client/fetch-seam';
import {
  BLOG_TEMPLATE_ROUTES,
  createMarketingApp,
  createMarketingCmsClient,
  createHttpAnalyticsTransport,
  createLocalStorageAdapter,
} from '@puffin-core/marketing-shell';
import { StorageService, STORAGE_TYPE } from '@puffin-core/storage';
import { getBrand } from '@puffin-core/brand-assets';
import { logger } from '@puffin-core/logging-browser';

import i18n from './i18n/i18n';
import buildMarketingChromeDescriptor from './marketingChrome';
import LandingPage from './pages/LandingPage';
import FeaturesPage from './pages/FeaturesPage';
import PricingPage from './pages/PricingPage';
import ContactPage from './pages/ContactPage';
import MarketingLoginPage from './pages/MarketingLoginPage';
import NotFoundPage from './pages/NotFoundPage';
import LegalPage from './pages/LegalPage';
import { PATROL_MARKETING_ROUTES } from './constants/patrolMarketingRoutePaths';

const APP_KEY = 'patrol';

/**
 * The ONE reporter for this site's analytics transports (REQUEST-VALIDATION-CONVERGE-001 AN2): a lost
 * event is reported, never swallowed — at `warn`, because a lost analytics event is not an error page.
 * `logger` is the browser logger this marketing entry's own `createAppLogger` bootstrap configured
 * (`./telemetry.js`); the app bundle's `core/config/logger` would re-bootstrap it over the app's
 * api-client, which the prerendered marketing bundle must not carry.
 *
 * @param {Error} failure - what the transport could not deliver
 * @returns {void}
 */
function reportAnalyticsFailure(failure) {
  logger.warn('[marketing-analytics] an analytics event was not delivered', {
    error: failure.message,
  });
}

export function createApp({ staticLocation } = {}) {
  // The factory wraps with <I18nextProvider>, so descendant pages get useTranslation().
  // Pre-resolve brand metadata to confirm the appKey is registered.
  // `getBrand` throws on unknown keys — startup-time guard so a
  // misconfigured deploy fails loud rather than at first render.
  getBrand(APP_KEY);

  // Phase 28 K.2/K.3 — marketing-CMS client for the 4 blog routes.
  // `createFetchSeam()` is the canonical lazy `globalThis.fetch` binding (RES-CLIENT-001
  // Q-TRANSPORT-DISEAM): resolved at CALL time, so jsdom-mode tests that stub after module load
  // still win — the property the inline arrow here existed for.
  const cmsClient = createMarketingCmsClient({
    appKey: APP_KEY,
    fetch: createFetchSeam({ label: 'patrolMarketingWebsiteApp' }),
  });

  return createMarketingApp({
    appKey: APP_KEY,
    // MSSG-001 — set at BUILD time by the prerender plugin; undefined for every browser mount.
    staticLocation,
    i18n,
    defaultLocale: 'en',
    cmsClient,
    routes: [
      { path: '/', element: <LandingPage /> },
      { path: '/features', element: <FeaturesPage /> },
      { path: '/pricing', element: <PricingPage /> },
      { path: '/contact', element: <ContactPage /> },
      // MKT-LOGIN-XDOMAIN-001 — cross-domain login at www.patrolpuffin.com/login.
      { path: '/login', element: <MarketingLoginPage /> },
      // Phase 28 K.4 — blog routes (4 template entries dispatched via
      // BlogTemplateRoute → BlogIndexPage / BlogPostPage / etc.). The four
      // {path,template} pairs now come from the shared `BLOG_TEMPLATE_ROUTES`
      // SSOT (BLOG-REMEDIATE-001 WS-C) instead of being hardcoded per-app.
      ...BLOG_TEMPLATE_ROUTES,
      { path: '/privacy', element: <LegalPage documentKey="privacy" /> },
      { path: '/terms', element: <LegalPage documentKey="terms" /> },
      { path: '*', element: <NotFoundPage /> },
    ],
    // The chrome's descriptor BUILDER — resolved per render in the language in force (Q-LC24-7).
    chrome: { descriptor: buildMarketingChromeDescriptor, storage: StorageService.getInstance() },
    cookieConsent: {
      storageKey: 'patrolpuffinCookieConsent',
      storage: StorageService.getInstance(),
      STORAGE_TYPE_LOCAL: STORAGE_TYPE.LOCAL,
      // The banner's privacy link: this site's OWN public privacy route, from the portal's one
      // route registry (a missing one throws at module load; see createMarketingApp).
      privacyHref: PATROL_MARKETING_ROUTES.PRIVACY,
    },
    analytics: {
      transport: createHttpAnalyticsTransport({
        endpoint: '/api/v1/analytics/ingest',
        onError: reportAnalyticsFailure,
      }),
      storageAdapter: createLocalStorageAdapter(),
      onTransportError: reportAnalyticsFailure,
    },
  });
}

const MarketingWebsiteApp = createApp();

export { MarketingWebsiteApp };
export default MarketingWebsiteApp;
